import { Attributes, IModel, IModelConfiguration, Transport } from '@pulse/common';

export interface PulseGlobals {
    (cmd: string): void;
    (cmd: string, ...args: unknown[]): void;
}

export interface PulseGlobals {
    /**
     * Configurations
     */
    c?: Partial<Record<string, unknown>>;

    /**
     * Message queue to be used until library loads.
     */
    q?: Array<{ cmd: string; args: unknown[] }>;

    // /**
    //  * User provided dataLayer
    //  */
    // l?: any[];

    init: (...params: unknown[]) => Promise<{
        settings?: Record<string, unknown>;
        tracker?: Record<string, unknown>;
    }>;

    tracker?: Record<string, unknown>;
    settings?: Record<string, unknown>;
    settingsFactory?: Record<string, unknown>;

    /**
     * Initial document URL
     */
    idu?: string;

    _loaded: Record<string, number>;
    _initialized?: boolean;
    _initializStarted?: boolean;
    initTask: Promise<Record<string, unknown>>;
    _v?: number;
    _attributes?: Attributes;
    _route?: IModel<IModelConfiguration, IModelConfiguration>;
    _transport: Transport;
}

export interface NativeResourceLock {
    name: string;
    mode: 'exclusive' | 'shared';
    clientId?: string;
}

export interface NavigatorLockManagerSnapshot {
    held: NativeResourceLock[];
    pending: NativeResourceLock[];
}

export interface NavigatorLockRequestOptions {
    signal?: AbortSignal;
    mode?: 'exclusive' | 'shared';
    ifAvailable?: boolean;
    steal?: boolean;
}

export interface NavigatorLockManager {
    request(
        name: string,
        callback: (lock: NativeResourceLock) => Promise<void>,
    ): Promise<NavigatorLockManagerSnapshot>;
    request(
        name: string,
        options: NavigatorLockRequestOptions,
        callback: (lock: NativeResourceLock) => Promise<void>,
    ): Promise<NavigatorLockManagerSnapshot>;
    query(): Promise<NavigatorLockManagerSnapshot>;
}

type NavigatorUAData = {
    platform: string;
    brands: { brand: string; version: string }[];
    mobile?: boolean;
};

export interface PulseNavigator extends Navigator {
    deviceMemory?: number;
    userAgentData?: NavigatorUAData;
    locks: NavigatorLockManager;
    connection: {
        effectiveType?: string;
        rtt?: number;
        downlink?: number;
        saveData?: boolean;
    };
}

export interface PulseDocument extends Document {
    __native_cookie: string;
}

export interface PulseWindow extends Window {
    /**
     * Script host base URL
     */
    pulseScriptBaseUrl?: string;

    /**
     * @deprecated use `pulse` instead!
     */
    paa?: PulseGlobals;
    pulse?: PulseGlobals;
    _pulse?: Partial<PulseGlobals>;
    dataLayer?: Array<Record<string, unknown>>;
    XMLHttpRequest: { new (): XMLHttpRequest };
    Date: DateConstructor;
    navigator: PulseNavigator;
    AbortController: new () => AbortController;
    env?: Record<string, unknown>;
    Event: typeof Event;
    StorageEvent: new (type: 'storage', eventInitDict?: StorageEventInit) => StorageEvent;
    document: PulseDocument;
    Document: {
        new (): Document;
        prototype: Document;
    };
    BroadcastChannel: {
        prototype: BroadcastChannel;
        new (name: string): BroadcastChannel;
    };

    pulseReady: Array<{
        sdk: 'settings' | 'tracker' | 'mini';
        onReady: () => void;
    }>;

    pulseInit: Array<{
        sdk: 'settings' | 'tracker' | 'mini';
        onReady: () => void;
    }>;

    PerformanceObserver: typeof PerformanceObserver;
    ReadableStream: typeof ReadableStream;
    URLSearchParams: typeof URLSearchParams;
    Blob: typeof Blob;
    ArrayBuffer: typeof ArrayBuffer;
}

export class PulseEnvironment {
    win: PulseWindow;
    doc: PulseDocument;
    navigator: PulseNavigator;
    landingUrl: string;

    constructor(win: PulseWindow) {
        this.win = win as PulseWindow;
        this.doc = this.win.document;
        this.navigator = this.win.navigator;
        this.landingUrl = this.getInitialDocumentURL();
    }

    private getInitialDocumentURL(): string {
        if (typeof this.win.performance?.getEntries === 'function') {
            const entries = this.win.performance.getEntries();
            const nav = entries.find((i) => i.entryType === 'navigation');

            if (nav && this.isValidURL(nav.name)) {
                return nav.name;
            }
        }

        if (this.isValidURL(this.win.pulse?.idu)) {
            return this.win.pulse.idu;
        }

        return this.win.document.location.href;
    }

    private isValidURL(url: unknown): url is string {
        if (typeof url !== 'string') {
            return false;
        }

        try {
            const u = new URL(url);

            return Boolean(u.hostname);
        } catch {
            return false;
        }
    }
}
